import { Stack } from '@mui/material';
import type {
  DynamicLineItem,
  FixedDynamicLineItem,
  UsageDynamicLineItem,
} from '@vy/dynamic-invoice-builder';
import { isFixedDynamicLineItem, isUsageDynamicLineItem } from '@vy/dynamic-invoice-builder';
import { buildCurrencyLevel } from '@vy/services';
import { isEmptyArray, isNotEmptyArray } from '@vy/utils';
import { FC, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useDeleteLineItem } from '../../../../../hooks/requests/use-delete-line-item';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { invalidateInvoicesQueries } from '../../../../../utils/query-invalidation';
import { runMutation } from '../../../../../utils/vy-mutation';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import { DynamicInvoiceViewSection } from '../../dynamic-invoice-view-components/DynamicInvoiceViewSection';
import { useSortedDynamicLineItems } from '../../hooks/use-sorted-dynamic-line-items';
import { FixedFeeItem, UsageItemAccordion } from '../components';
import { RevisionedFeeItem } from '../components/RevisionedFeeItem';

export const UsageLinesContainer: FC = () => {
  const { dynamicInvoiceData } = useDynamicInvoice();
  const { dynamicLineItems } = dynamicInvoiceData.invoice;
  const { measurementChartData } = dynamicInvoiceData;
  const invoiceId = dynamicInvoiceData.invoice.id;

  const queryClient = useQueryClient();
  const deleteLineItemMutation = useDeleteLineItem();

  const sortedLineItems = useSortedDynamicLineItems(dynamicLineItems as DynamicLineItem[] | undefined);

  const { revisionedLineItems, regularFixedLineItems, usageLineItems } = useMemo(() => {
    const revisioned: FixedDynamicLineItem[] = [];
    const regularFixed: FixedDynamicLineItem[] = [];
    const usage: UsageDynamicLineItem[] = [];

    for (const lineItem of sortedLineItems) {
      if (isFixedDynamicLineItem(lineItem)) {
        if (lineItem.isRevised) {
          revisioned.push(lineItem);
        } else {
          regularFixed.push(lineItem);
        }
      }

      if (isUsageDynamicLineItem(lineItem)) {
        usage.push(lineItem);
      }
    }

    return { revisionedLineItems: revisioned, regularFixedLineItems: regularFixed, usageLineItems: usage };
  }, [sortedLineItems]);

  const onDeleteLineItem = async (lineItemId: string) => {
    await runMutation(
      deleteLineItemMutation,
      { lineItemId },
      {
        pending: 'Deleting line item...',
        success: 'Deleted line item',
        error: 'Failed to delete',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId)),
            invalidateInvoicesQueries(queryClient),
          ]);
        },
      },
    );
  };

  const baseCurrency = buildCurrencyLevel({
    invoiceCurrency: dynamicInvoiceData.invoice.baseCurrency,
    contractCurrency: dynamicInvoiceData.contract?.currencyOverride,
  });

  if (isEmptyArray(sortedLineItems)) {
    return null;
  }

  return (
    <>
      {isNotEmptyArray(revisionedLineItems) && (
        <DynamicInvoiceViewSection title="Revisions">
          <Stack direction="column" spacing={2} sx={{
            maxWidth: "100%"
          }}>
            {revisionedLineItems.map((lineItem) => (
              <RevisionedFeeItem
                key={`revision-${lineItem.lineItemId}`}
                lineItem={lineItem}
                currency={dynamicInvoiceData.invoice.currency}
                onDelete={onDeleteLineItem}
              />
            ))}
          </Stack>
        </DynamicInvoiceViewSection>
      )}
      {isNotEmptyArray(regularFixedLineItems) && (
        <DynamicInvoiceViewSection title="Fixed fees">
          <Stack direction="column" spacing={2} sx={{
            maxWidth: "100%"
          }}>
            {regularFixedLineItems.map((lineItem) => (
              <FixedFeeItem
                key={`fixed-${lineItem.productId ?? lineItem.name}`}
                lineItem={lineItem}
                currency={dynamicInvoiceData.invoice.currency}
              />
            ))}
          </Stack>
        </DynamicInvoiceViewSection>
      )}
      {isNotEmptyArray(usageLineItems) && (
        <DynamicInvoiceViewSection title="Usage Fees">
          <Stack direction="column" spacing={2} sx={{
            maxWidth: "100%"
          }}>
            {usageLineItems.map((lineItem, index) => (
              <UsageItemAccordion
                key={`usage-${lineItem.productId}`}
                lineItem={lineItem}
                defaultExpanded={index === 0}
                measurementChartData={measurementChartData}
                customerId={dynamicInvoiceData.customer.id}
                billingCycle={dynamicInvoiceData.invoice.billingCycle}
                currency={dynamicInvoiceData.invoice.currency}
                baseCurrency={baseCurrency}
              />
            ))}
          </Stack>
        </DynamicInvoiceViewSection>
      )}
    </>
  );
};

export default UsageLinesContainer;
